/**
 * Paying in a popup window.
 *
 * OLG's hosted card form used to take over the tab; the event page and its
 * sheet were gone until OLG redirected back. Now, where a real window can
 * open, the form opens in a small popup while the page waits, and when OLG
 * redirects the popup back to us the popup tells the page and closes
 * itself. The popup shares the browser's first-party cookies, so someone
 * signed in to OLG stays signed in there.
 *
 * Rules learned the hard way:
 *  - A popup must be opened synchronously inside the click handler or the
 *    browser blocks it, so it opens first (with a placeholder) and gets its
 *    URL once the API answers.
 *  - Phones turn popups into tabs and cannot post back reliably, so small
 *    screens keep the in-place redirect.
 *  - The message from the popup is a hint, like OLG's token: the page still
 *    reads the invoice from MP before it says "paid".
 */

export const RETURN_MESSAGE = 'wbc-payment-return';

export interface PaymentReturn {
  invoiceGuid: string | null;
  claimedSuccess: boolean;
}

/** Whether this browser should pay in a popup rather than by leaving the page. */
export function popupPaymentsWanted(): boolean {
  if (typeof window === 'undefined') return false;
  const wide = window.matchMedia?.('(min-width: 768px)').matches ?? false;
  const mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  return wide && !mobile;
}

export interface PaymentWindow {
  /** Send the popup to OLG once the API has minted the token. */
  setUrl(url: string): void;
  /** Bring the popup to the front (the "Don't see the payment window?" answer). */
  focus(): void;
  close(): void;
  readonly closed: boolean;
}

const BRAND_CSS = `html,body{height:100%;margin:0;font-family:Montserrat,ui-sans-serif,system-ui,sans-serif;background:#f5f6f7;color:#1c2b39;-webkit-font-smoothing:antialiased}
main{min-height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:32px 24px;box-sizing:border-box}
.mark{font-weight:900;letter-spacing:.18em;font-size:11px;color:#78828a;text-transform:uppercase;margin-bottom:28px}
.disc{width:56px;height:56px;border-radius:50%;display:flex;align-items:center;justify-content:center;margin-bottom:18px}
.spin{width:36px;height:36px;border-radius:50%;border:3px solid #cdd1d4;border-top-color:#299c8f;animation:s .9s linear infinite}
@keyframes s{to{transform:rotate(360deg)}}
h1{font-size:20px;font-weight:800;margin:0 0 8px;line-height:1.2}p{margin:0;font-size:13px;line-height:1.55;color:#78828a;max-width:34ch}
.lock{display:inline-flex;align-items:center;gap:6px;margin-top:22px;padding:8px 14px;border-radius:999px;background:#fff;border:1px solid #cdd1d4;font-size:12px;font-weight:700;color:#1c2b39}
.btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;margin-top:22px;padding:12px 22px;border-radius:999px;border:0;background:#299c8f;color:#fff;font:inherit;font-size:14px;font-weight:700;cursor:pointer}
.btn.quiet{background:#fff;color:#1c2b39;border:1px solid #cdd1d4;margin-top:10px}
.note{margin-top:26px;font-size:11px;color:#78828a}`;

const PLACEHOLDER = `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>Secure payment · Woodside Bible Church</title>
<style>${BRAND_CSS}</style></head>
<body><main><div class="mark">Woodside Bible Church</div><div class="spin"></div><h1>Opening your secure payment</h1>
<p>Our payment provider, Online Giving, will load here in a moment. Your seats are already held.</p>
<span class="lock">&#128274; onlinegiving.org</span>
<p class="note">If this window does not change in a few seconds, close it and choose "Pay here instead" on the event page.</p></main></body></html>`;

/**
 * THE MP SESSION, CARRIED TO ONLINE GIVING
 * ========================================
 *
 * Online Giving signs somebody in from an `mp_token` on the URL. Everywhere
 * else on woodsidebible.org that is done by OLG's own `mpsso.js` in the site
 * header — but it cannot reach us, for two separate reasons, and it is worth
 * writing both down because fixing either one alone would not have worked:
 *
 *   1. It runs ONCE, at DOMContentLoaded, attaching click listeners to the
 *      anchors that exist at that moment. Every link this widget renders to
 *      OLG appears minutes later, after somebody has submitted, so there is
 *      no listener on it. The popup has no anchor at all — it is opened with
 *      `window.open` and `location.replace`.
 *   2. It finds those anchors with `document.querySelectorAll`, which does
 *      not cross a shadow boundary. This widget lives in a shadow root, so
 *      its links are invisible to the script even on a fresh page load.
 *
 * (Its other path, `silent_sso`, is the MP-widget-only one: it fires for
 * `<mpp-checkout>` elements, which we are not.)
 *
 * So a registrant arrived at OLG as a stranger — signed in on our page and
 * signed out on theirs, no saved payment methods, name and email blank on a
 * form they had just filled in.
 *
 * This does what mpsso.js does, at the one moment we control. Same token
 * (`mpp-widgets_AuthToken`), same `jwt:` shape, unencoded — a JWT is already
 * URL-safe and OLG's own handler writes it raw.
 *
 * ONLY TO ONLINE GIVING, only when there is a token, and never twice.
 * Somebody signed out pays as a guest exactly as before, and a URL pointing
 * anywhere else — the mock checkout, some future provider — is handed back
 * untouched rather than being given somebody's session.
 */
const OLG_HOSTS = /(^|\.)(onlinegiving\.org|onlinegiving\.cc)$/i;

export function withMpSso(url: string): string {
  try {
    const token = localStorage.getItem('mpp-widgets_AuthToken');
    if (!token || token.trim() === '' || token.trim().toLowerCase() === 'null') return url;
    const parsed = new URL(url, window.location.href);
    if (!OLG_HOSTS.test(parsed.hostname)) return url;
    if (parsed.searchParams.has('mp_token')) return url;
    // Built by hand rather than with searchParams.set, which percent-encodes
    // the colon. OLG's own script writes `mp_token=jwt:<token>` literally.
    return url + (url.includes('?') ? '&' : '?') + 'mp_token=jwt:' + token;
  } catch {
    // No storage, or a URL that will not parse. Paying as a guest still works.
    return url;
  }
}

/**
 * Open the payment window now, empty, sized for a card form. Returns null
 * when the browser blocked it, in which case the caller navigates in place.
 */
export function openPaymentWindow(): PaymentWindow | null {
  // OLG's page is a full site page, not a card form: give it room to sign
  // in and read, on the biggest window that still reads as a window.
  const availW = window.screen?.availWidth || window.outerWidth || 1280;
  const availH = window.screen?.availHeight || window.outerHeight || 900;
  const w = Math.min(1000, Math.max(720, availW - 160));
  const h = Math.min(920, Math.max(680, availH - 120));
  const left = Math.max(0, Math.round((window.screenX ?? 0) + ((window.outerWidth ?? w) - w) / 2));
  const top = Math.max(0, Math.round((window.screenY ?? 0) + ((window.outerHeight ?? h) - h) / 2));
  const features = `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=yes,status=no`;
  const win = window.open('', 'wbc-payment', features);
  if (!win) return null;
  try {
    win.document.open();
    win.document.write(PLACEHOLDER);
    win.document.close();
  } catch {
    /* a browser that refuses document.write on about:blank still navigates */
  }
  return {
    setUrl(url) {
      const withSession = withMpSso(url);
      try {
        win.location.replace(withSession);
      } catch {
        win.location.href = withSession;
      }
      win.focus();
    },
    focus() {
      try {
        win.focus();
      } catch {
        /* nothing to focus */
      }
    },
    close() {
      try {
        if (!win.closed) win.close();
      } catch {
        /* already gone */
      }
    },
    get closed() {
      return win.closed;
    },
  };
}

/** Listen for the popup's return. Same origin only; the payload is a hint. */
export function onPaymentReturn(handler: (r: PaymentReturn) => void): () => void {
  const listener = (e: MessageEvent) => {
    if (e.origin !== window.location.origin) return;
    const d = e.data as { type?: string; invoiceGuid?: unknown; claimedSuccess?: unknown } | null;
    if (!d || d.type !== RETURN_MESSAGE) return;
    handler({
      invoiceGuid: typeof d.invoiceGuid === 'string' ? d.invoiceGuid : null,
      claimedSuccess: d.claimedSuccess === true,
    });
  };
  window.addEventListener('message', listener);
  return () => window.removeEventListener('message', listener);
}

/**
 * Inside the popup, back from OLG: tell the page that opened us and close.
 * Returns false when there is no opener to tell (a normal tab), so the
 * caller renders the page as usual.
 */
export function announceReturnToOpener(r: PaymentReturn): boolean {
  const opener = window.opener as Window | null;
  if (!opener || opener.closed) return false;
  try {
    opener.postMessage({ type: RETURN_MESSAGE, ...r }, window.location.origin);
  } catch {
    return false;
  }
  const ok = r.claimedSuccess;
  document.documentElement.innerHTML = `<head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>${ok ? 'Payment received' : 'Back to the event'} · Woodside Bible Church</title>
<style>${BRAND_CSS}</style></head>
<body><main><div class="mark">Woodside Bible Church</div>
<div class="disc" style="background:${ok ? '#299c8f' : '#eeeff0'};color:${ok ? '#fff' : '#1c2b39'};font-size:26px">${ok ? '&#10003;' : '&#8592;'}</div>
<h1>${ok ? 'Payment received' : 'Nothing was charged'}</h1>
<p>${ok ? 'Thank you. The event page is updating with your registration, and a receipt is on its way from Online Giving.' : 'The payment was not completed. Your seats stay held for a while; the event page has a button to try again or pay later.'}</p>
<button type="button" class="btn" id="wbc-back">Back to the event page</button>
<button type="button" class="btn quiet" id="wbc-close">Close this window</button>
<p class="note">This window closes on its own in a moment.</p></main></body>`;
  const back = () => {
    try {
      opener.focus();
    } catch {
      /* fine */
    }
    try {
      window.close();
    } catch {
      /* fine */
    }
  };
  document.getElementById('wbc-back')?.addEventListener('click', back);
  document.getElementById('wbc-close')?.addEventListener('click', back);
  setTimeout(back, 1800);
  return true;
}
